import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import styled from 'styled-components';
import { AuthContext } from "../../context/AuthContext";
import toast from 'react-hot-toast';

const Container = styled.div`
 display: flex;
 padding-top: 8rem;
 flex-direction: column;
 align-items: center;
 justify-content: center;
`;

const Card = styled.div`
 width: 400px;
 max-width: 850px;
 background: white;
 border-radius: 8px;
 box-shadow: 1px 2px 15px -5px rgb(0 0 0 / 1.4);
`;

const CardHeader = styled.div`
 padding: 24px;
 text-align: center;
`;

const CardTitle = styled.h2`
 font-size: 24px;
 font-weight: 600;
 margin-bottom: 8px;
`;

const CardDescription = styled.p`
 color: #666;
 font-size: 14px;
`;

const Form = styled.form`
 padding: 24px;
 display: flex;
 flex-direction: column;
 gap: 16px;
`;

const InputGroup = styled.div`
 display: flex;
 flex-direction: column;
 gap: 8px;
`;

const Label = styled.label`
 font-size: 14px;
 font-weight: 500;
`;

const Input = styled.input`
 padding: 8px;
 border: 1px solid #e2e8f0;
 border-radius: 6px;
 width: calc(100% - 16px);
`;

const Select = styled.select`
 padding: 8px;
 border: 1px solid #e2e8f0;
 border-radius: 6px;
 width: 100%;
`;

const RadioGroup = styled.div`
 display: flex;
 gap: 16px;
 align-items: center;
`;

const RadioLabel = styled.label`
 display: flex;
 align-items: center;
 gap: 4px;
 cursor: pointer;
`;

const BirthGroup = styled.div`
 display: grid;
 grid-template-columns: repeat(3, 1fr);
 gap: 8px;
`;

const Button = styled.button`
 width: 100%;
 padding: 8px;
 background: #000;
 color: white;
 border: none;
 border-radius: 6px;
 cursor: pointer;
 font-weight: 500;
 
 &:disabled {
   opacity: 0.7;
 }
`;

function RegisterView() {
   const [registerId, setRegisterId] = useState("");
   const [password, setPassword] = useState("");
   const [confirmPassword, setConfirmPassword] = useState("");
   const [gender, setGender] = useState("");
   const [birthYear, setBirthYear] = useState("");
   const [birthMonth, setBirthMonth] = useState("");
   const [birthDay, setBirthDay] = useState("");
   const [message, setMessage] = useState("");
   const [isLoading, setIsLoading] = useState(false);
   const navigate = useNavigate();

   const validateForm = () => {
       if (registerId.length === 0) {
           setMessage("아이디를 입력해주세요.");
           return false;
       }
       if (password.length === 0) {
           setMessage("비밀번호를 입력해주세요.");
           return false;
       }
       if (password !== confirmPassword) {
           setMessage("비밀번호가 일치하지 않습니다.");
           return false;
       }
       return true;
   };

   const handleSubmitRegister = async (event) => {
       event.preventDefault();
       if (!validateForm()) return;

       setIsLoading(true);
       try {
           const response = await fetch('/request/register', {
               method: 'POST',
               headers: { 'Content-Type': 'application/json' },
               body: JSON.stringify({
                   user_id: registerId,
                   user_pwd: password,
                   gender: gender,
                   birth_y: birthYear,
                   birth_m: birthMonth,
                   birth_d: birthDay
               })
           });

           if (!response.ok) {
               const errorText = await response.text();
               throw new Error(errorText || response.statusText);
           }

           toast.success('회원가입이 성공적으로 완료되었습니다.');
           navigate('/login');
       } catch (error) {
           console.error("회원가입 실패: ", error);
           setMessage(error.message || "회원가입에 실패하였습니다.");
       } finally {
           setIsLoading(false);
       }
   };

   return (
       <Container>
           <Card>
               <CardHeader>
                   <CardTitle>회원가입</CardTitle>
                   <CardDescription>AntsNest에 오신 것을 환영합니다</CardDescription>
               </CardHeader>
               <Form onSubmit={handleSubmitRegister}>
                   {message && <p style={{color: 'red', textAlign: 'center'}}>{message}</p>}
                   
                   <InputGroup>
                       <Label htmlFor="registerId">아이디</Label>
                       <Input
                           id="registerId"
                           type="text"
                           value={registerId}
                           onChange={(e) => setRegisterId(e.target.value)}
                           required
                       />
                   </InputGroup>

                   <InputGroup>
                       <Label htmlFor="password">비밀번호</Label>
                       <Input
                           id="password"
                           type="password"
                           value={password}
                           onChange={(e) => setPassword(e.target.value)}
                           required
                       />
                   </InputGroup>

                   <InputGroup>
                       <Label htmlFor="confirmPassword">비밀번호 확인</Label>
                       <Input
                           id="confirmPassword"
                           type="password"
                           value={confirmPassword}
                           onChange={(e) => setConfirmPassword(e.target.value)}
                           required
                       />
                   </InputGroup>

                   <InputGroup>
                       <Label>성별</Label>
                       <RadioGroup>
                           <RadioLabel>
                               <input
                                   type="radio"
                                   name="gender"
                                   value="1"
                                   checked={gender === '1'}
                                   onChange={(e) => setGender(e.target.value)}
                               />
                               남성
                           </RadioLabel>
                           <RadioLabel>
                               <input
                                   type="radio"
                                   name="gender"
                                   value="2"
                                   checked={gender === '2'}
                                   onChange={(e) => setGender(e.target.value)}
                               />
                               여성
                           </RadioLabel>
                       </RadioGroup>
                   </InputGroup>

                   <InputGroup>
                       <Label>생년월일</Label>
                       <BirthGroup>
                           <Input
                               type="text"
                               placeholder="YYYY"
                               value={birthYear}
                               onChange={(e) => setBirthYear(e.target.value)}
                               maxLength={4}
                           />
                           <Select
                               value={birthMonth}
                               onChange={(e) => setBirthMonth(e.target.value)}
                           >
                               <option value="">월 선택</option>
                               {Array.from({ length: 12 }, (_, i) => {
                                   const month = (i + 1).toString().padStart(2, '0');
                                   return (
                                       <option key={month} value={month}>
                                           {month}월
                                       </option>
                                   );
                               })}
                           </Select>
                           <Input
                               type="text"
                               placeholder="DD"
                               value={birthDay}
                               onChange={(e) => setBirthDay(e.target.value)}
                               maxLength={2}
                           />
                       </BirthGroup>
                   </InputGroup>

                   <Button type="submit" disabled={isLoading}>
                       {isLoading ? "가입 중..." : "회원가입"}
                   </Button>
                   
                   <Link to="/login" style={{textAlign: 'center', color: '#666', textDecoration: 'none'}}>
                       이미 계정이 있으신가요? 로그인
                   </Link>
               </Form>
           </Card>
       </Container>
   );
}

export default RegisterView;